import React from "react";
import { SCHEMA, getScenarioMeta } from "../../data/reportSchema";
import ToneSelector from "../wizard/ToneSelector";

export default function CopilotSidebar({
  selectedScenario,
  formData,
  onChangeForm,
  onGenerate,
  isLoading,
  onReset,
  onOpenHistory
}) {
  const dynamicWizards = SCHEMA.dynamic_wizards;
  const targetScenario = selectedScenario || "Custom_Issue";
  const scenarioMeta = getScenarioMeta(targetScenario);
  const questions = dynamicWizards[targetScenario] || [];

  return (
    <div className="flex flex-col h-full bg-slate-900/60 text-slate-200">
      {/* ── Header ── */}
      <div className="p-6 border-b border-white border-opacity-10 backdrop-blur-md sticky top-0 z-20 flex justify-between items-center bg-slate-900/80">
        <div>
          <h1 className="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
            Nexus Copilot
          </h1>
          <p className="text-xs text-slate-400 mt-1 font-medium">Enterprise Report Engine V5.0</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={onOpenHistory}
            className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition"
            title="History"
          >
            📋
          </button>
          <button 
            onClick={onReset}
            className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition"
            title="New"
          >
            🔄
          </button>
        </div>
      </div>

      {/* ── Scrollable Body ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-8">
        
        {/* Form Inputs */}
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-semibold text-indigo-300 uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
              Context Setup
            </h2>
          </div>
          
          <div className="px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700/50 mb-6 flex items-center gap-3">
            <span className="text-2xl">{scenarioMeta?.icon || "✨"}</span>
            <div>
              <span className="text-xs font-medium text-slate-400 block">Report Type</span>
              <span className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
                자유 기획 (Custom Issue)
              </span>
            </div>
          </div>

          <div className="mb-6">
            <ToneSelector 
              tone={formData.tone || "samsung"} 
              onChange={(newTone) => onChangeForm("tone", newTone)} 
            />
          </div>

          <div className="space-y-6">
            {questions.map((field) => {
              const value = formData[field.id] || "";
              return (
                <div key={field.id} className="group">
                  <label className="block text-sm font-medium text-slate-300 mb-1.5 group-hover:text-indigo-300 transition-colors">
                    {field.question} {field.required && <span className="text-rose-400">*</span>}
                  </label>
                  {field.hint && <p className="text-xs text-slate-500 mb-3">{field.hint}</p>}
                  
                  <textarea
                    value={value}
                    onChange={(e) => onChangeForm(field.id, e.target.value)}
                    placeholder={field.placeholder}
                    rows={4}
                    className="w-full glass-input p-3 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Footer / Generate Action ── */}
      <div className="p-6 border-t border-white border-opacity-10 bg-slate-900/90 backdrop-blur-md sticky bottom-0 z-20">
        <button
          onClick={onGenerate}
          disabled={isLoading}
          className="relative w-full overflow-hidden group rounded-xl p-0.5 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
        >
          <span className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 opacity-70 group-hover:opacity-100 animate-gradient-x blur-sm"></span>
          <div className="relative bg-slate-900 px-6 py-4 rounded-[11px] flex items-center justify-center gap-2">
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-bold text-sm bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-purple-300">
                  Generating C-Level Report...
                </span>
              </>
            ) : (
              <>
                <span className="font-bold text-sm bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-purple-300 group-hover:from-indigo-200 group-hover:to-purple-200">
                  Generate Report
                </span>
                <span className="text-indigo-400 group-hover:translate-x-1 transition-transform">→</span>
              </>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}
